import type { VisitorPageInput } from "@/lib/graphql/generated/types";
import type { SendVisitorMessageMutationFn } from "@/lib/graphql/queries/generated/messaging.generated";

/**
 * An OLDER BE does not know `VisitorSendMessageInput.page` and answers a GraphQL validation error that
 * names it. Remembered for the page session so later sends do not hit the error again.
 */
let pageUnsupported = false;

/** Test seam. */
export function resetPageSupport() {
  pageUnsupported = false;
}

// Only a schema validation error about the field itself (`Field "page" is not defined by type ...`,
// `Unknown field "page"`, `VisitorPageInput`), never any message that happens to contain the word: a
// false match would resend a message the BE may already have stored.
const PAGE_FIELD = /(["'`]page["'`]|VisitorPageInput)/;

function messagesOf(x: unknown): string[] {
  const o = (x ?? {}) as {
    errors?: readonly { message?: string }[];
    graphQLErrors?: readonly { message?: string }[];
    networkError?: { result?: { errors?: readonly { message?: string }[] } };
  };
  const all = [
    ...(o.errors ?? []),
    ...(o.graphQLErrors ?? []),
    ...(o.networkError?.result?.errors ?? []),
  ];
  return all.map((e) => e?.message ?? "");
}

function isPageError(x: unknown): boolean {
  return messagesOf(x).some((m) => PAGE_FIELD.test(m));
}

type Options = Parameters<SendVisitorMessageMutationFn>[0];
type Result = Awaited<ReturnType<SendVisitorMessageMutationFn>>;

/**
 * `sendVisitorMessage` with the host `page` attached. An input that already carries `page` (a queued
 * send captured it when it was made) keeps it; a send without one gets `page` (captured by the caller
 * now). A page validation error retries ONCE without `page` and turns it off for the session: the
 * visitor's text is never lost, any other error is returned/thrown untouched.
 */
export async function sendWithPage(args: {
  send: SendVisitorMessageMutationFn;
  options: Options;
  page: VisitorPageInput | undefined;
}): Promise<Result> {
  const input = args.options?.variables?.input;
  if (!input) return args.send(args.options);
  const { page: queued, ...bare } = input;
  const page = queued ?? args.page;
  const withVars = (i: typeof input): Options => ({
    ...args.options,
    variables: { ...args.options?.variables, input: i },
  });
  if (pageUnsupported || !page) return args.send(withVars(bare));
  let res: Result;
  try {
    res = await args.send(withVars({ ...bare, page }));
  } catch (err) {
    if (!isPageError(err)) throw err;
    pageUnsupported = true;
    return args.send(withVars(bare));
  }
  if (!isPageError(res)) return res;
  pageUnsupported = true;
  return args.send(withVars(bare));
}
